/* eslint-disable jsx-a11y/mouse-events-have-key-events */
// TODO Handle accessibility for mouse over events
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import AnimateHeight from 'react-animate-height';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Checkbox from '@mui/material/Checkbox';
import Tooltip from '@mui/material/Tooltip';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import FlagIcon from '@mui/icons-material/Flag';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';

import getCompsColumns from './compsTableColumns';
import { compIdentifier, isFlagOn } from '../compsHelpers';

import './CompTableRow.scss';

export default function CompTableRow({
  compData,
  setCompData,
  deleteCompById,
  rowIndex,
  tabSelected,
  setShowConfirmationModal,
  filters,
  valuationDate,
  reSummarize,
  descriptionsToShow,
  setDescriptionsToShow,
  descriptionsRef,
  checkboxesRef,
  subCompScroll,
  overlayScrollbarRef,
  overlayScroll,
}) {
  const [shortDescBtnFocused, setShortDescBtnFocused] = useState(-1);
  const [shortDescBtnHovered, setShortDescBtnHovered] = useState(-1);
  const [longDescBtnFocused, setLongDescBtnFocused] = useState(-1);
  const [longDescBtnHovered, setLongDescBtnHovered] = useState(-1);

  const [checkboxHovered, setCheckboxHovered] = useState(-1);

  const [rowHovered, setRowHovered] = useState(-1);

  const row = compData[rowIndex] || {};
  const displayName = String(row.Name ?? '').replace(/ *\([^)]*\) */g, '').trim();
  const isActive = isFlagOn(row.Active);
  const isMultiples = isFlagOn(row.Multiples);
  const isOutlier = isFlagOn(row.Outlier);
  const isChecked = tabSelected === 'comps' ? isActive && isMultiples : isActive;

  const handleDeleteCompany = (companyIdentifier) => {
    const targetId = compIdentifier(companyIdentifier);
    if (!targetId) return;
    if (typeof deleteCompById === 'function') {
      deleteCompById(targetId);
      return;
    }
    // Fallback if parent handler missing
    if (typeof setCompData === 'function') {
      setCompData((prev) => {
        const list = Array.isArray(prev) ? prev : compData;
        return list.filter((company, index) => (
          index === 0 || compIdentifier(company) !== targetId
        ));
      });
    }
  };

  const rowClassName = (index) => {
    if (overlayScroll) return 'comp-table-row-data overlay-scroll sub-company odd';
    if (index === 0) return 'comp-table-row-data sub-company odd';
    let className = 'comp-table-row-data';
    if (rowHovered === index) className += ' row-hovered';
    if ((tabSelected === 'comps' && !isMultiples) ||
      (tabSelected === 'industry' && !isActive)) className += ' red-inactive-row';
    if (tabSelected === 'comps' && isMultiples && !isActive) className += ' yellow-multiples-disabled-row';
    if ((index + 1) % 2 === 0) className += ' even';
    else className += ' odd';
    return className;
  };

  const extractCompVal = (val) => Array.isArray(val) ? val[0] : val;

  const hasValue = (val) => val !== null && val !== undefined && val !== '';

  const growthPeriodMap = {
    LFY: 'LFY-1',
    LTM: 'LTM-1',
    CFY: 'LFY',
    NFY: 'CFY',
    'NFY+1': 'NFY',
    'NFY+2': 'NFY+1',
    NTM: 'LTM',
  };

  const getDerivedMetricValue = (field) => {
    if (!row) return null;

    const marginMatch = field.match(/^(EBITDA|Gross) Margin \| (.+)$/);
    if (marginMatch) {
      const metric = marginMatch[1] === 'EBITDA' ? 'EBITDA' : 'Gross Profit';
      const period = marginMatch[2];
      const numerator = extractCompVal(row[`${metric} | ${period}`]);
      const denominator = extractCompVal(row[`Revenue | ${period}`]);
      if (typeof numerator === 'number' && typeof denominator === 'number' && denominator !== 0) {
        return numerator / denominator;
      }
      return null;
    }

    const growthMatch = field.match(/^(Revenue|EBITDA|Gross Profit) Growth \| (.+)$/);
    if (growthMatch) {
      const metric = growthMatch[1];
      const period = growthMatch[2];
      const prevPeriod = growthPeriodMap[period];
      if (!prevPeriod) return null;
      const currentValue = extractCompVal(row[`${metric} | ${period}`]);
      const previousValue = extractCompVal(row[`${metric} | ${prevPeriod}`]);
      if (typeof currentValue === 'number' && typeof previousValue === 'number' && previousValue !== 0) {
        return (currentValue - previousValue) / Math.abs(previousValue);
      }
    }

    return null;
  };

  const getDisplayValue = (field) => {
    const rawValue = extractCompVal(row[field]);
    if (hasValue(rawValue)) return rawValue;
    return getDerivedMetricValue(field);
  };

  return (
    <div className="CompTableRow">
      <div
        className={rowClassName(rowIndex)}
        onMouseOver={() => { if (rowHovered !== rowIndex) setRowHovered(rowIndex); }}
        onMouseLeave={() => setRowHovered(-1)}
      >
        <div className={`fixed-left ${rowClassName(rowIndex)}`}>
          <div className="row-data-group comp-list">
            {rowIndex !== 0 ? (
              <>
                <div
                  className="table-cell"
                  onMouseOver={() => { if (checkboxHovered !== rowIndex) setCheckboxHovered(rowIndex); }}
                  onMouseLeave={() => setCheckboxHovered(-1)}
                >
                  <Tooltip
                    PopperProps={{ className: 'bottom-arrow-tooltip select-deselect' }}
                    title={
                      isChecked ? (
                        <span>
                          Click to remove
                        </span>
                      ) : tabSelected === 'comps' && !isActive ? (
                        <span>
                          Automatically removed when
                          <br />
                          removed from volatility tab.
                        </span>
                      ) : (
                        <span>
                          Click to include
                        </span>
                      )
                    }
                    enterDelay={500}
                    enterNextDelay={1500}
                    placement="top"
                    arrow
                  >
                    <Checkbox
                      inputRef={rowIndex !== 0 ? checkboxesRef.current[rowIndex] : null}
                      className={tabSelected === 'comps' && isMultiples && !isActive ? 'yellow' : ''}
                      checked={isChecked}
                      onChange={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        // Yellow state: Multiples on but Active off — confirm before re-adding
                        if (tabSelected === 'comps' && isMultiples && !isActive) {
                          setShowConfirmationModal(rowIndex);
                          return;
                        }
                        reSummarize(rowIndex, tabSelected === 'industry' ? 'active' : 'multiple');
                      }}
                      onClick={(e) => e.stopPropagation()}
                      icon={<div className="remove-check-icon"><RemoveRoundedIcon /></div>}
                      checkedIcon={<div className="checked-icon"><CheckRoundedIcon /></div>}
                    />
                  </Tooltip>
                </div>
                <div className="table-cell" style={{ minWidth: '243px' }}>
                  {displayName.length > 41 ? (
                    <Tooltip title={displayName} placement="top">
                      <span>{`${displayName.slice(0, 41).trim()}...`}</span>
                    </Tooltip>
                  ) : (
                    displayName
                  )}
                </div>
                <div className="table-cell actions-cell">
                  {isOutlier ? (
                    <>
                      <IconButton
                        className="outlier-flag-icon is-outlier"
                        onClick={(e) => {
                          e.stopPropagation();
                          reSummarize(rowIndex, 'outlier');
                        }}
                      >
                        <FlagIcon />
                      </IconButton>
                      <Tooltip
                        disableInteractive
                        title="Delete Company"
                        PopperProps={{ className: 'bottom-arrow-tooltip' }}
                        placement="top"
                        enterDelay={500}
                        enterNextDelay={1000}
                        arrow
                      >
                        <IconButton
                          className={`outlier-flag-icon${rowHovered !== rowIndex ? ' hidden' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteCompany(row.Identifier);
                          }}
                        >
                          <DeleteOutlinedIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  ) : (
                    <>
                      <Tooltip
                        disableInteractive
                        title="Flag as outlier"
                        PopperProps={{ className: 'bottom-arrow-tooltip' }}
                        placement="top"
                        enterDelay={500}
                        enterNextDelay={1000}
                        arrow
                      >
                        <IconButton
                          className={`outlier-flag-icon${rowHovered !== rowIndex ? ' hidden' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            reSummarize(rowIndex, 'outlier');
                          }}
                        >
                          <FlagOutlinedIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        disableInteractive
                        title="Delete Company"
                        PopperProps={{ className: 'bottom-arrow-tooltip' }}
                        placement="top"
                        enterDelay={500}
                        enterNextDelay={1000}
                        arrow
                      >
                        <IconButton
                          className={`outlier-flag-icon${rowHovered !== rowIndex ? ' hidden' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteCompany(row.Identifier);
                          }}
                        >
                          <DeleteOutlinedIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="table-cell" />
                <div className="table-cell">{row.Name}</div>
                <div className="table-cell" />
              </>
            )}
          </div>
        </div>
        <div className="right-scrollable" ref={overlayScroll ? overlayScrollbarRef : rowIndex === 0 ? subCompScroll : null}>
          {getCompsColumns(compData, tabSelected, filters, valuationDate)
            .map((columnGroup) => (
              <div key={columnGroup.columnGroupName} className="row-data-group">
                {columnGroup.columnGroupChildren.map((column) => {
                  if (column.field === 'Short Description' || column.field === 'Long Description') return (
                    <div key={column.field} className="table-cell description-btn" style={{ minWidth: `${column.width}px` }}>
                      {rowIndex !== 0 ? (
                        <Button
                          onMouseDown={(e) => {
                            if (descriptionsToShow[rowIndex] !== (column.field === 'Short Description' ? 'short' : 'long')) {
                              const newDescriptionToShow = [...descriptionsToShow];
                              newDescriptionToShow[rowIndex] = (column.field === 'Short Description' ? 'short' : 'long');
                              setDescriptionsToShow(newDescriptionToShow);
                            } else {
                              const newDescriptionToShow = [...descriptionsToShow];
                              newDescriptionToShow[rowIndex] = '';
                              setDescriptionsToShow(newDescriptionToShow);
                            }
                            if (!e.target.className.includes('Mui-focusVisible')) e.preventDefault();
                          }}
                          onFocus={() => column.field === 'Short Description' ? setShortDescBtnFocused(rowIndex) : setLongDescBtnFocused(rowIndex)}
                          onBlur={() => column.field === 'Short Description' ? setShortDescBtnFocused(-1) : setLongDescBtnFocused(-1)}
                          onMouseOver={() => {
                            if (column.field === 'Short Description' && shortDescBtnHovered !== rowIndex) setShortDescBtnHovered(rowIndex);
                            if (column.field === 'Long Description' && longDescBtnHovered !== rowIndex) setLongDescBtnHovered(rowIndex);
                          }}
                          onMouseLeave={() => column.field === 'Short Description' ? setShortDescBtnHovered(-1) : setLongDescBtnHovered(-1)}
                        >
                          {descriptionsToShow[rowIndex] === (column.field === 'Short Description' ? 'short' : 'long') ? 'Hide' : 'View'}
                        </Button>
                      ) : '-'}
                    </div>
                  );

                  if (column.field === 'Industry') return (
                    <div key={column.field} className="table-cell" style={{ minWidth: `${column.width}px` }}>
                      {row[column.field] && row[column.field].length >= 18 ? (
                        <Tooltip title={row[column.field]} placement="top">
                          <span>{`${row[column.field].slice(0, 18).trim()}...`}</span>
                        </Tooltip>
                      ) : row[column.field]}
                    </div>
                  );

                  const compVal = getDisplayValue(column.field);
                  const safeVal = (
                    column.field === 'Identifier' &&
                    (rowIndex === 0 || compVal === 'HEADER')
                  ) ? '' : compVal;
                  return (
                    <div
                      key={column.field}
                      className="table-cell"
                      style={{ minWidth: `${column.width}px` }}
                    >
                      {column.format ? column.format(safeVal) : safeVal || '-'}
                    </div>
                  );
                })}
              </div>
            ))}
        </div>
      </div>
      <AnimateHeight
        duration={300}
        height={(descriptionsToShow[rowIndex] === 'long' || descriptionsToShow[rowIndex] === 'short') ? 'auto' : 0}
      >
        <div className={`comp-description${(rowIndex + 1) % 2 === 0 ? ' even' : ' odd'}`}>
          {descriptionsToShow[rowIndex] === 'short' && (
            <div ref={rowIndex !== 0 ? descriptionsRef.current[rowIndex] : null}>
              <p
                className={`description${shortDescBtnFocused === rowIndex ? ' focused' : ''}` +
                  `${shortDescBtnHovered === rowIndex ? ' hovered' : ''}`}
              >
                {row['Short Description']}
              </p>
            </div>
          )}
          {descriptionsToShow[rowIndex] === 'long' && (
            <div ref={rowIndex !== 0 ? descriptionsRef.current[rowIndex] : null}>
              <p
                className={`description${longDescBtnFocused === rowIndex ? ' focused' : ''}` +
                  `${longDescBtnHovered === rowIndex ? ' hovered' : ''}`}
              >
                {row['Long Description']}
              </p>
            </div>
          )}
          <div className="comp-description-background" />
        </div>
      </AnimateHeight>
    </div>
  );
}

CompTableRow.propTypes = {
  compData: PropTypes.array.isRequired,
  setCompData: PropTypes.func,
  deleteCompById: PropTypes.func,
  tabSelected: PropTypes.string.isRequired,
  filters: PropTypes.object.isRequired,
  setShowConfirmationModal: PropTypes.func,
  subCompScroll: PropTypes.object,
  reSummarize: PropTypes.func,
  descriptionsToShow: PropTypes.array,
  setDescriptionsToShow: PropTypes.func,
  descriptionsRef: PropTypes.object,
  checkboxesRef: PropTypes.object,
  valuationDate: PropTypes.string,
  rowIndex: PropTypes.number.isRequired,
  overlayScrollbarRef: PropTypes.func,
  overlayScroll: PropTypes.bool,
};

CompTableRow.defaultProps = {
  setCompData: undefined,
  deleteCompById: undefined,
  subCompScroll: {},
  descriptionsRef: {},
  checkboxesRef: {},
  descriptionsToShow: [],
  setDescriptionsToShow: () => { },
  setShowConfirmationModal: () => { },
  reSummarize: () => { },
  overlayScrollbarRef: () => { },
  overlayScroll: false,
};
